// My CRM · My quotes — status, value, how complete each quote is, and Lara analysis (needs the completeness bar).
import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import LaraPanel from "../../../components/lara/LaraPanel";
import { useMyCrm, Chips, SearchBox, SortSelect, useDebounced, Pager, Empty, th, td, gbp, dshort, ago } from "./_shared";

const STATUS_TONE = { Draft: "text-slate-300", Sent: "text-sky-300", Paid: "text-emerald-300", Rejected: "text-rose-300", Archived: "text-slate-500" };

function Completeness({ r, min }) {
  const tone = r.completeness >= 70 ? "bg-emerald-400" : r.completeness >= min ? "bg-amber-400" : "bg-rose-400";
  return (
    <div title={r.missing.length ? `Missing: ${r.missing.join(", ")}` : "Fully filled in"} data-testid={`mycrm-qu-complete-${r.id}`}>
      <div className="flex items-center gap-2"><span className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden"><span className={`block h-full ${tone}`} style={{ width: `${r.completeness}%` }} /></span>
        <span className="font-mono text-[11px]">{r.completeness}%</span></div>
      {r.missing.length > 0 && <div className="text-[10.5px] text-slate-500 truncate max-w-[180px]">Add: {r.missing.slice(0, 2).join(", ")}</div>}
    </div>
  );
}

export default function QuotesTab({ amId }) {
  const [status, setStatus] = useState(["Draft", "Sent"]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created");
  const [page, setPage] = useState(1);
  const [lara, setLara] = useState(null);
  const dq = useDebounced(q);
  const { data, loading, reload } = useMyCrm("quotes", { am_id: amId, status: status.join(","), q: dq, sort, page });
  const fs = data?.facets?.status || {};
  const min = data?.min_completeness ?? 40;
  return (
    <div className="space-y-3" data-testid="mycrm-quotes">
      <div className="flex flex-wrap items-center gap-2">
        <Chips testid="mycrm-qu-status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} items={["Draft", "Sent", "Paid", "Rejected", "Archived"].filter((k) => fs[k]).map((k) => [k, k, fs[k]])} />
        <div className="ml-auto flex flex-wrap gap-2">
          <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search ref, customer, company, title…" testid="mycrm-qu-search" />
          <SortSelect value={sort} onChange={setSort} testid="mycrm-qu-sort" options={[["created", "Newest"], ["value", "Value"], ["complete", "Completeness"], ["customer", "Customer"]]} />
        </div>
      </div>
      <p className="text-[11.5px] text-slate-500">Lara can analyse a quote once it's at least {min}% filled in, so her advice has something to go on. Hover a greyed-out Analyse button to see why it can't run yet.</p>
      <div className="ws-card overflow-x-auto">
        {loading && !data ? <Empty text="Loading…" /> : !data.rows.length ? <Empty text="No quotes match." /> : (
          <table className="w-full">
            <thead><tr>{["Quote", "Customer", "Status", "Value", "Created", "Filled in", "Lara", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`mycrm-qu-row-${r.id}`}>
                  <td className={td}><Link to={`/quotes/${r.id}/edit`} className="font-mono text-amber-200 hover:underline" data-testid={`mycrm-qu-open-${r.id}`}>{r.ref || "Quote"}</Link>
                    {r.title && <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{r.title}</div>}</td>
                  <td className={td}>{r.customer}{r.company && <div className="text-[11px] text-slate-500">{r.company}</div>}</td>
                  <td className={`${td} ${STATUS_TONE[r.status] || ""}`}>{r.status}</td>
                  <td className={`${td} font-mono`}>{gbp(r.total)}<div className="text-[10.5px] text-slate-500">{r.lines} line{r.lines === 1 ? "" : "s"}</div></td>
                  <td className={`${td} text-[11.5px]`}>{dshort(r.created)}</td>
                  <td className={td}><Completeness r={r} min={min} /></td>
                  <td className={`${td} text-[11.5px]`}>{r.lara_at ? <span className="text-fuchsia-300">Analysed {ago(r.lara_at)}</span> : <span className="text-slate-600">—</span>}</td>
                  <td className={`${td} text-right`}>
                    <button disabled={!r.lara_ready} onClick={() => setLara(r)} title={r.lara_ready ? "Open Lara's analysis" : r.lara_reason || `Needs ${min}%+ filled in`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200 text-[11px] disabled:opacity-35 disabled:cursor-not-allowed" data-testid={`mycrm-qu-lara-${r.id}`}>
                      <Sparkles className="w-3 h-3" />{r.lara_at ? "View" : "Analyse"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && <Pager page={page} total={data.total} onPage={setPage} />}
      </div>
      {lara && <LaraPanel quoteId={lara.id} quoteRef={lara.ref} onClose={() => { setLara(null); reload(); }} />}
    </div>
  );
}
